import { Component, Element, Prop, h, Host, Watch, State } from '@stencil/core';
import { alert_exclamation_circle, check_circle } from 'pn-design-assets/pn-assets/icons.js';
import { uuidv4 } from '@/globals/helpers';

@Component({
  tag: 'pn-progress-bar',
  styleUrl: 'pn-progress-bar.scss',
})
export class PnProgressBar {
  uniqueId: string = uuidv4();

  idProgress: string = `pn-progress-bar-${this.uniqueId}`;
  idLabel: string = `${this.idProgress}-label`;
  idText: string = `${this.idProgress}-text`;

  @Element() hostElement: HTMLElement;

  @State() progressPercentage: string;

  /** Set a label for the progress bar */
  @Prop() label?: string;
  /** Progress value, 0-100 */
  @Prop() progress: number = 0;
  /** Replaces the default progress percentage text */
  @Prop() progressText?: string;
  /** Text shown on success (100%)  */
  @Prop() successText?: string;
  /** Trigger an error and display a message */
  @Prop() error?: string;
  /** A unique HTML id fror the progress bar */
  @Prop() progressId: string = this.idProgress;

  @Watch('progress')
  setProgress(): void {
    if (!Number.isInteger(this.progress)) return;

    const progressWidth = this.progress < 100 ? Math.ceil(this.progress) : 100;

    this.progressPercentage = `${progressWidth.toString()}%`;

    this.hostElement.style.setProperty('--progress-value', this.progressPercentage);
  }

  componentWillLoad() {
    this.setProgress();
  }

  hasLabel(): boolean {
    return !!this.label?.length;
  }

  hasText(): boolean {
    return !!this.successText?.length;
  }

  hasError(): boolean {
    return !!this.error?.length;
  }

  isValid(): boolean {
    return this.progress === 100;
  }

  hasIcon(): boolean {
    return this.isValid() || this.hasError();
  }

  getSubtext() {
    if (this.hasError()) return this.error;
    if (this.isValid() && this.hasText()) return this.successText;

    return this.progressText ? this.progressText : this.progressPercentage;
  }

  getIconSvg() {
    if (this.hasError()) return alert_exclamation_circle;
    return check_circle;
  }

  getIconColor() {
    if (this.hasError()) return 'warning';
    return 'green500';
  }

  render() {
    return (
      <Host>
        {this.hasLabel() && (
          <p id={this.idLabel} class="pn-progress-bar-label">
            <span>{this.label}</span>
            {this.hasIcon() && <pn-icon icon={this.getIconSvg()} color={this.getIconColor()}></pn-icon>}
          </p>
        )}
        <div
          id={this.progressId}
          class="pn-progress-bar-container"
          role="progressbar"
          aria-labelledby={this.idLabel}
          aria-describedby={this.hasIcon() ? this.idText : null}
          aria-valuenow={this.progress}
          aria-valuetext={this.progressPercentage}
        >
          <div class="pn-progress-bar-meter">
            <div class="pn-progress-bar-value" data-error={this.hasError()} data-valid={this.isValid()}></div>
          </div>
        </div>
        <p id={this.idText} class="pn-progress-bar-subtext" role={this.hasError() ? 'alert' : null}>
          <span>{this.getSubtext()}</span>
        </p>
      </Host>
    );
  }
}
